import { useSelector } from "react-redux";

import type { RootState } from "@/lib/types/state";

import type { FeatureFlag } from "../types";

type UseFeatureFlagResult = {
  hasLoaded: boolean;
  isEnabled: boolean | undefined;
};

/**
 * Resolves a feature flag value and its loading state from Redux.
 *
 * Checks private flags first, then public flags. `hasLoaded` reflects only
 * the store that contains the flag — avoiding unnecessary waits when e.g. a
 * public flag is queried before private flags have loaded (pre-login contexts).
 * If the flag is absent from both stores, waits for both to finish loading
 * before returning `hasLoaded: true` so the caller can fall back to
 * `defaultFlagBehavior`.
 */
export function useFeatureFlag(flagName: FeatureFlag): UseFeatureFlagResult {
  const privateFlags = useSelector((state: RootState) => state.flags.privateFlags);
  const publicFlags = useSelector((state: RootState) => state.flags.publicFlags);

  const privateFlagsLoaded = useSelector((state: RootState) => state.flags.privateFlagsHaveLoaded);
  const publicFlagsLoaded = useSelector((state: RootState) => state.flags.publicFlagsHaveLoaded);

  const isEnabled = privateFlags?.[flagName] ?? publicFlags?.[flagName];

  const isPrivateFlag = privateFlags != null && flagName in privateFlags;
  const isPublicFlag = publicFlags != null && flagName in publicFlags;

  let hasLoaded: boolean;

  if (isPrivateFlag) hasLoaded = privateFlagsLoaded;
  else if (isPublicFlag) hasLoaded = publicFlagsLoaded;
  else hasLoaded = privateFlagsLoaded && publicFlagsLoaded;

  return { hasLoaded, isEnabled };
}
